import apiClient, { type ApiResponse } from './client';

export interface DemoStudioSession {
  id: string;
  organization_id: string;
  created_by_user_id: string;
  title?: string | null;
  status: string;
  browserbase_session_id?: string | null;
  browserbase_region?: string | null;
  live_view_url?: string | null;
  fullscreen_live_view_url?: string | null;
  planned_walkthrough_json?: Record<string, unknown> | null;
  raw_recording_path?: string | null;
  raw_audio_path?: string | null;
  raw_dom_recording_path?: string | null;
  visible_start_ms?: number | null;
  narration_start_ms?: number | null;
  completed_at?: string | null;
  error_message?: string | null;
  organization_video_id?: string | null;
  /**
   * Knowledge-gap lineage. Non-null when this session was launched from a
   * knowledge gap's "Capture demo" action. Used to render the "From
   * knowledge gap" badge on the Demo Studio session list.
   */
  linked_knowledge_gap_summary_id?: string | null;
  created_at: string;
  updated_at: string;
}

export interface DemoStudioAudioUploadResponse {
  audio_url: string;
  session: DemoStudioSession;
}

export interface DemoStudioFileUploadResponse {
  status: string;
  filename: string;
}

export interface DemoStudioUploadedFilesResponse {
  files: string[];
}

export interface FileChooserStatusResponse {
  pending: boolean;
  mode?: string | null;
}

export interface ResolveFileChooserResponse {
  status: string;
}

export interface DemoStudioSessionListResponse {
  sessions: DemoStudioSession[];
  total: number;
  limit: number;
  offset: number;
}

export interface CreateDemoStudioSessionRequest {
  organization_id: string;
  title?: string;
  timezone?: string;
  initial_url?: string;
}

class DemoStudioService {
  async createSession(
    payload: CreateDemoStudioSessionRequest,
  ): Promise<ApiResponse<DemoStudioSession>> {
    return apiClient.post<DemoStudioSession>('/api/demo-studio/sessions/', payload);
  }

  async stopSession(
    sessionId: string,
    clientElapsedMs?: number,
  ): Promise<ApiResponse<DemoStudioSession>> {
    return apiClient.post<DemoStudioSession>(`/api/demo-studio/sessions/${sessionId}/stop`, {
      ...(clientElapsedMs != null ? { client_elapsed_ms: clientElapsedMs } : {}),
    });
  }

  async getSession(sessionId: string): Promise<ApiResponse<DemoStudioSession>> {
    return apiClient.get<DemoStudioSession>(`/api/demo-studio/sessions/${sessionId}`);
  }

  async listSessions(
    organizationId: string,
    params?: { limit?: number; offset?: number },
  ): Promise<ApiResponse<DemoStudioSessionListResponse>> {
    return apiClient.get<DemoStudioSessionListResponse>('/api/demo-studio/sessions/', {
      organization_id: organizationId,
      limit: params?.limit ?? 20,
      offset: params?.offset ?? 0,
    });
  }

  async uploadAudio(
    sessionId: string,
    file: File,
    segmentIndex?: number,
  ): Promise<ApiResponse<DemoStudioAudioUploadResponse>> {
    const params = segmentIndex != null ? `?segment_index=${segmentIndex}` : '';
    return apiClient.uploadFile<DemoStudioAudioUploadResponse>(
      `/api/demo-studio/sessions/${sessionId}/audio${params}`,
      file,
    );
  }

  async reconnectSession(sessionId: string): Promise<ApiResponse<DemoStudioSession>> {
    return apiClient.post<DemoStudioSession>(
      `/api/demo-studio/sessions/${sessionId}/reconnect`,
    );
  }

  async pauseSession(
    sessionId: string,
    clientElapsedMs?: number,
  ): Promise<ApiResponse<DemoStudioSession>> {
    return apiClient.post<DemoStudioSession>(
      `/api/demo-studio/sessions/${sessionId}/pause`,
      { ...(clientElapsedMs != null ? { client_elapsed_ms: clientElapsedMs } : {}) },
    );
  }

  async resumeSession(
    sessionId: string,
    clientElapsedMs?: number,
  ): Promise<ApiResponse<DemoStudioSession>> {
    return apiClient.post<DemoStudioSession>(
      `/api/demo-studio/sessions/${sessionId}/resume`,
      { ...(clientElapsedMs != null ? { client_elapsed_ms: clientElapsedMs } : {}) },
    );
  }

  async keepaliveSession(sessionId: string): Promise<ApiResponse<DemoStudioSession>> {
    return apiClient.post<DemoStudioSession>(
      `/api/demo-studio/sessions/${sessionId}/keepalive`,
    );
  }

  async markTimingEvent(
    sessionId: string,
    eventType: 'visible' | 'narration_start',
    clientElapsedMs?: number,
  ): Promise<ApiResponse<DemoStudioSession>> {
    return apiClient.post<DemoStudioSession>(`/api/demo-studio/sessions/${sessionId}/timing`, {
      event_type: eventType,
      ...(clientElapsedMs != null ? { client_elapsed_ms: clientElapsedMs } : {}),
    });
  }

  async retryFinalization(sessionId: string): Promise<ApiResponse<DemoStudioSession>> {
    return apiClient.post<DemoStudioSession>(
      `/api/demo-studio/sessions/${sessionId}/retry-finalization`,
    );
  }

  async uploadFileToBrowser(
    sessionId: string,
    file: File,
  ): Promise<ApiResponse<DemoStudioFileUploadResponse>> {
    return apiClient.uploadFile<DemoStudioFileUploadResponse>(
      `/api/demo-studio/sessions/${sessionId}/upload-file`,
      file,
    );
  }

  async getUploadedFiles(
    sessionId: string,
  ): Promise<ApiResponse<DemoStudioUploadedFilesResponse>> {
    return apiClient.get<DemoStudioUploadedFilesResponse>(
      `/api/demo-studio/sessions/${sessionId}/uploaded-files`,
    );
  }

  async getFileChooserStatus(
    sessionId: string,
  ): Promise<ApiResponse<FileChooserStatusResponse>> {
    return apiClient.get<FileChooserStatusResponse>(
      `/api/demo-studio/sessions/${sessionId}/file-chooser-status`,
    );
  }

  async resolveFileChooser(
    sessionId: string,
    filenames: string[],
  ): Promise<ApiResponse<ResolveFileChooserResponse>> {
    return apiClient.post<ResolveFileChooserResponse>(
      `/api/demo-studio/sessions/${sessionId}/resolve-file-chooser`,
      { filenames },
    );
  }

  async cancelFileChooser(
    sessionId: string,
  ): Promise<ApiResponse<ResolveFileChooserResponse>> {
    return apiClient.post<ResolveFileChooserResponse>(
      `/api/demo-studio/sessions/${sessionId}/cancel-file-chooser`,
    );
  }
}

const demoStudioService = new DemoStudioService();

export default demoStudioService;
